import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Card as CardType } from '../types';
import Card from '../components/Card';


// List of popular card brands
const cardBrands = [
  'All Brands',
  'Panini',
  'Topps',
  'Leaf',
  'Upper Deck',
  'Fleer',
  'Bowman',
  'Donruss',
  'Prizm',
  'Select',
  'Mosaic',
  'Optic',
  'National Treasures',
  'Immaculate',
  'Chronicles',
  'Revolution',
];

// Grade options by company
const gradeOptions = [
  { company: 'PSA', grades: ['10', '9', '8', '7', '6', '5'] },
  { company: 'BGS', grades: ['10', '9.5', '9', '8.5', '8'] },
  { company: 'SGC', grades: ['10', '9.5', '9', '8.5', '8'] }
];

const CardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<CardType[]>([]);
  const [filteredCards, setFilteredCards] = useState<CardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('All Brands');
  const [displayCount, setDisplayCount] = useState(20);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
  const [yearRange, setYearRange] = useState<[number, number]>([1980, 2025]);
  const [isGradedFilter, setIsGradedFilter] = useState<string>('all');
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    search: true,
    sport: true,
    brand: true,
    gradedStatus: true,
    gradeOptions: false,
    priceRange: true,
    yearRange: true,
  });
  // Selected grade filters
  const [selectedGrades, setSelectedGrades] = useState<{ [key: string]: boolean }>({});
  const [gradeFilterString, setGradeFilterString] = useState<string>('');

  // Reference to track scroll position
  const lastScrollTop = useRef<number>(0);

  // Effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const st = window.scrollY || document.documentElement.scrollTop;
      if (st < lastScrollTop.current) {
        // Scrolling up - close all sections
        setExpandedSections({
          search: false,
          sport: false,
          brand: false,
          gradedStatus: false,
          gradeOptions: false,
          priceRange: false,
          yearRange: false,
        });
      }
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle section expansion changes
  const handleSectionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  // Handle grade checkbox changes
  const handleGradeChange = (grade: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedGrades = {
      ...selectedGrades,
      [grade]: event.target.checked
    };
    
    setSelectedGrades(newSelectedGrades);
    
    // Update the grade filter string based on selected grades
    const selectedGradesList = Object.keys(newSelectedGrades).filter(key => newSelectedGrades[key]);
    const newGradeFilterString = selectedGradesList.join(',');
    setGradeFilterString(newGradeFilterString);
  };

  useEffect(() => {
    const loadCards = async () => {
      try {
        setLoading(true);
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Load cards from localStorage
        const savedCards = localStorage.getItem('adminCards');
        if (savedCards) {
          const allCards = JSON.parse(savedCards);
          // Filter for cards marked as posted
          const postedCards = allCards.filter((card: CardType) => card.isPosted);
          setCards(postedCards);
        } else {
          setCards([]);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load cards. Please try again later.');
        setCards([]);
      } finally {
        setLoading(false);
      }
    };

    loadCards();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = cards;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        card =>
          card.player.toLowerCase().includes(query) ||
          card.team.toLowerCase().includes(query) ||
          card.brand.toLowerCase().includes(query) ||
          card.title.toLowerCase().includes(query)
      );
    }

    if (sportFilter !== 'all') {
      filtered = filtered.filter(card => card.sport === sportFilter);
    }
    
    if (brandFilter !== 'All Brands') {
      filtered = filtered.filter(card => 
        card.brand.toLowerCase().includes(brandFilter.toLowerCase())
      );
    }
    
    // Apply price range filter
    filtered = filtered.filter(
      card => card.price >= priceRange[0] && card.price <= priceRange[1]
    );
    
    // Apply year range filter
    filtered = filtered.filter(
      card => card.year >= yearRange[0] && card.year <= yearRange[1]
    );
    
    // Apply graded status filter
    if (isGradedFilter !== 'all') {
      filtered = filtered.filter(
        card => isGradedFilter === 'graded' ? card.isGraded : !card.isGraded
      );

      // Apply specific grade filters if selected
      if (isGradedFilter === 'graded' && gradeFilterString) {
        const selectedGrades = gradeFilterString.split(',');
        filtered = filtered.filter(card => {
          if (!card.grade) return false;
          
          return selectedGrades.some(selectedGrade => 
            card.grade?.toLowerCase().includes(selectedGrade.toLowerCase())
          );
        });
      }
    }

    setFilteredCards(filtered);
  }, [cards, searchQuery, sportFilter, brandFilter, priceRange, yearRange, isGradedFilter, gradeFilterString]);

  // Reset grade selection when switching away from graded only
  useEffect(() => {
    if (isGradedFilter !== 'graded') {
      setSelectedGrades({});
      setGradeFilterString('');
      
      // Close the grade options section
      setExpandedSections(prev => ({
        ...prev,
        gradeOptions: false
      }));
    } else {
      // Open the grade options section when "Graded Only" is selected
      setExpandedSections(prev => ({
        ...prev,
        gradeOptions: true
      }));
    }
  }, [isGradedFilter]);

  const handleCardClick = (card: CardType) => {
    navigate(`/card/${card.id}`);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => Math.min(prev + 20, filteredCards.length));
  };

  const handleSportFilterChange = (event: SelectChangeEvent) => {
    setSportFilter(event.target.value);
  };
  
  const handleBrandFilterChange = (event: SelectChangeEvent) => {
    setBrandFilter(event.target.value);
  };
  
  const handleIsGradedChange = (event: SelectChangeEvent) => {
    setIsGradedFilter(event.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSportFilter('all');
    setBrandFilter('All Brands');
    setPriceRange([0, 50000]);
    setYearRange([1980, 2025]);
    setIsGradedFilter('all');
    setSelectedGrades({});
    setGradeFilterString('');
  };

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const displayedCards = filteredCards.slice(0, displayCount);

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Box sx={{ mb: 6, maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
          Browse the Goat Collection
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Premium sports cards from the Goat Cards collection
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
        {/* Filters Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '25%' } }}>
          <Paper sx={{ 
            p: 3, 
            position: 'sticky', 
            top: 20,
            maxHeight: 'calc(100vh - 40px)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Filters</Typography>
              <Button size="small" onClick={handleClearFilters}>
                Clear All
              </Button>
            </Box>
            
            {/* Scrollable filter content */}
            <Box sx={{ 
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                }
              },
              pr: 1 // Add padding right to prevent content being covered by scrollbar
            }}>
              {/* Search */}
              <Accordion 
                expanded={expandedSections.search} 
                onChange={handleSectionChange('search')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Search</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    placeholder="Search cards..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </AccordionDetails>
              </Accordion>
              
              {/* Sport Filter */}
              <Accordion 
                expanded={expandedSections.sport}
                onChange={handleSectionChange('sport')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Sport</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sport</InputLabel>
                    <Select
                      value={sportFilter}
                      label="Sport"
                      onChange={handleSportFilterChange}
                    >
                      <MenuItem value="all">All Sports</MenuItem>
                      <MenuItem value="basketball">Basketball</MenuItem>
                      <MenuItem value="football">Football</MenuItem>
                      <MenuItem value="baseball">Baseball</MenuItem>
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
              
              {/* Brand Filter */}
              <Accordion 
                expanded={expandedSections.brand}
                onChange={handleSectionChange('brand')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Brand</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={brandFilter}
                      label="Brand"
                      onChange={handleBrandFilterChange}
                    >
                      {cardBrands.map((brand) => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
              
              {/* Graded Status */}
              <Accordion
                expanded={expandedSections.gradedStatus}
                onChange={handleSectionChange('gradedStatus')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Graded Status</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <InputLabel>Graded Status</InputLabel>
                    <Select
                      value={isGradedFilter}
                      label="Graded Status"
                      onChange={handleIsGradedChange}
                    >
                      <MenuItem value="all">All Cards</MenuItem>
                      <MenuItem value="graded">Graded Only</MenuItem>
                      <MenuItem value="ungraded">Raw Cards Only</MenuItem>
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
              
              {/* Grade Options - Only visible when Graded Only is selected */}
              {isGradedFilter === 'graded' && (
                <Accordion
                  expanded={expandedSections.gradeOptions}
                  onChange={handleSectionChange('gradeOptions')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Grade Options</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {gradeOptions.map((company) => (
                      <Box key={company.company} sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {company.company}
                        </Typography>
                        <Grid container spacing={1}>
                          {company.grades.map((grade) => {
                            const gradeKey = `${company.company} ${grade}`;
                            return (
                              <Grid item xs={6} key={gradeKey}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={!!selectedGrades[gradeKey]}
                                      onChange={handleGradeChange(gradeKey)}
                                      size="small"
                                    />
                                  }
                                  label={`${company.company} ${grade}`}
                                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
              
              {/* Price Range */}
              <Accordion
                expanded={expandedSections.priceRange}
                onChange={handleSectionChange('priceRange')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Price Range</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ px: 1 }}>
                    <Typography gutterBottom>
                      {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
                    </Typography>
                    <Slider
                      value={priceRange}
                      onChange={(_, value) => setPriceRange(value as [number, number])}
                      valueLabelDisplay="auto"
                      min={0}
                      max={50000}
                      valueLabelFormat={formatPrice}
                      sx={{ mt: 2 }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* Year Range */}
              <Accordion
                expanded={expandedSections.yearRange}
                onChange={handleSectionChange('yearRange')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Year Range</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ px: 1 }}>
                    <Typography gutterBottom>
                      {yearRange[0]} - {yearRange[1]}
                    </Typography>
                    <Slider
                      value={yearRange}
                      onChange={(_, value) => setYearRange(value as [number, number])}
                      valueLabelDisplay="auto"
                      min={1980}
                      max={2025}
                      sx={{ mt: 2 }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Paper>
        </Box>
        
        {/* Cards Grid */}
        <Box sx={{ width: { xs: '100%', md: '75%' } }}>
          {/* Results count */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredCards.length} cards found
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 4 }}>
                {displayedCards.map((card) => (
                  <Box key={card.id}>
                    <Card card={card} onCardClick={handleCardClick} />
                  </Box>
                ))}
              </Box>

              {displayCount < filteredCards.length && (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleLoadMore}
                    sx={{ px: 5, py: 1.5, borderRadius: '8px' }}
                  >
                    Load More Cards
                  </Button>
                </Box>
              )}

              {filteredCards.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No cards found matching your criteria
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{ mt: 2 }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default CardsPage; 